import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Users, 
  Search, 
  MessageSquare, 
  Shield, 
  UserPlus,
  ArrowRight,
  Sparkles,
  Lock,
  Eye,
  EyeOff,
  FileText,
  Bell,
  AlertTriangle,
  TrendingUp,
  Activity
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityHub = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [hasProfile, setHasProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ connections: 0, pendingRequests: 0, groups: 0 });
  const [suggestions, setSuggestions] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);

  useEffect(() => {
    checkProfile();
  }, []);

  const checkProfile = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const response = await axios.get(`${API}/community/profile/check/exists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasProfile(response.data.has_profile);
      
      if (response.data.has_profile) {
        fetchStats();
        fetchSuggestions();
        fetchActivityFeed();
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const [connRes, reqRes] = await Promise.all([
        axios.get(`${API}/community/connections`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/community/connections/requests/incoming`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats({
        connections: connRes.data.connections?.length || 0,
        pendingRequests: reqRes.data.requests?.length || 0,
        groups: 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const response = await axios.get(`${API}/community/suggestions?limit=3`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const fetchActivityFeed = async () => {
    setFeedLoading(true);
    try {
      const token = localStorage.getItem('polibot_token');
      const response = await axios.get(`${API}/community/activity-feed?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityFeed(response.data.feed || []);
    } catch (error) {
      console.error('Error fetching activity feed:', error);
    } finally {
      setFeedLoading(false);
    }
  };

  const getFeedIcon = (type) => {
    switch (type) {
      case 'policy_alert': return <FileText className="h-4 w-4" />;
      case 'network_growth': return <TrendingUp className="h-4 w-4" />;
      case 'group_suggestion': return <Users className="h-4 w-4" />;
      case 'group_activity': return <UserPlus className="h-4 w-4" />;
      case 'connection_request': return <Bell className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getFeedColor = (type, riskScore) => {
    if (type === 'policy_alert') {
      if (riskScore >= 70) return 'bg-destructive/10 text-destructive border-destructive/30';
      if (riskScore >= 40) return 'bg-warning/10 text-warning border-warning/30';
      return 'bg-success/10 text-success border-success/30';
    }
    if (type === 'connection_request') return 'bg-primary/10 text-primary border-primary/30';
    if (type === 'group_suggestion') return 'bg-accent/10 text-accent border-accent/30';
    return 'bg-muted text-muted-foreground border-border';
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return language === 'es' ? 'ahora' : 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="community-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  // Show onboarding prompt if no profile
  if (!hasProfile) {
    return (
      <div className="max-w-4xl mx-auto space-y-8 animate-fade-in" data-testid="community-onboarding-prompt">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">
            {language === 'es' ? 'Centro de Conexión Comunitaria' : 'Community Connection Hub'}
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            {language === 'es' 
              ? 'Conecta con otros defensores en tu área que luchan por causas similares. Tu privacidad está protegida.'
              : 'Connect with other advocates in your area fighting for similar causes. Your privacy is protected.'}
          </p>
        </div>

        {/* Privacy Promise */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-2">
                  {language === 'es' ? 'Privacidad por Diseño' : 'Privacy by Design'}
                </h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• {language === 'es' ? 'Tú controlas qué información compartes' : 'You control what information you share'}</li>
                  <li>• {language === 'es' ? 'Los mensajes están encriptados' : 'Messages are encrypted'}</li>
                  <li>• {language === 'es' ? 'Sin rastreo de ubicación GPS' : 'No GPS location tracking'}</li>
                  <li>• {language === 'es' ? 'Botón de pánico para ocultar tu cuenta instantáneamente' : 'Panic button to instantly hide your account'}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features Preview */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <Search className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-1">
                {language === 'es' ? 'Descubrir Defensores' : 'Discover Advocates'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {language === 'es' 
                  ? 'Encuentra personas que luchan por las mismas causas'
                  : 'Find people fighting for the same causes'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <MessageSquare className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-1">
                {language === 'es' ? 'Mensajería Segura' : 'Secure Messaging'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {language === 'es' 
                  ? 'Comunícate de forma privada con conexiones'
                  : 'Communicate privately with connections'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Users className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-1">
                {language === 'es' ? 'Unirse a Grupos' : 'Join Groups'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {language === 'es' 
                  ? 'Colabora en coaliciones y campañas'
                  : 'Collaborate on coalitions and campaigns'}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="text-center">
          <Button size="lg" onClick={() => navigate('/community/onboarding')} data-testid="start-onboarding-btn">
            <Sparkles className="mr-2 h-5 w-5" />
            {language === 'es' ? 'Comenzar Configuración' : 'Start Setup'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            {language === 'es' ? 'Solo toma 2 minutos' : 'Only takes 2 minutes'}
          </p>
        </div>
      </div>
    );
  }

  // Main hub view for users with profiles
  return (
    <div className="space-y-6 animate-fade-in" data-testid="community-hub">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            {language === 'es' ? 'Centro de Conexión Comunitaria' : 'Community Connection Hub'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'es' 
              ? 'Conecta con defensores que luchan por las mismas causas'
              : 'Connect with advocates fighting for the same causes'}
          </p>
        </div>
        <Button onClick={() => navigate('/community/discover')} data-testid="discover-btn">
          <Search className="mr-2 h-4 w-4" />
          {language === 'es' ? 'Descubrir Defensores' : 'Discover Advocates'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/community/connections')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Mis Conexiones' : 'My Connections'}
                </p>
                <p className="text-3xl font-bold mt-1">{stats.connections}</p>
              </div>
              <Users className="h-8 w-8 text-primary opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/community/connections')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Solicitudes Pendientes' : 'Pending Requests'}
                </p>
                <p className="text-3xl font-bold mt-1">{stats.pendingRequests}</p>
              </div>
              <UserPlus className="h-8 w-8 text-primary opacity-80" />
            </div>
            {stats.pendingRequests > 0 && (
              <Badge className="mt-2 bg-accent/20 text-accent">
                {language === 'es' ? 'Requiere atención' : 'Needs attention'}
              </Badge>
            )}
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/community/messages')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Mensajes' : 'Messages'}
                </p>
                <p className="text-3xl font-bold mt-1">
                  <MessageSquare className="h-8 w-8 inline" />
                </p>
              </div>
              <Lock className="h-6 w-6 text-success opacity-80" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {language === 'es' ? 'Encriptados' : 'Encrypted'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <Card data-testid="suggestions-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {language === 'es' ? 'Sugerencias de Conexión' : 'Connection Suggestions'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {suggestions.map((suggestion, index) => (
                <Card key={index} className="border-dashed">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {Math.round(suggestion.relevance_score)}% {language === 'es' ? 'coincidencia' : 'match'}
                      </Badge>
                    </div>
                    <h4 className="font-medium text-sm">
                      {suggestion.profile.display_name || (language === 'es' ? 'Defensor Anónimo' : 'Anonymous Advocate')}
                    </h4>
                    <p className="text-xs text-muted-foreground mb-2">
                      {suggestion.profile.location_display || (language === 'es' ? 'Ubicación oculta' : 'Location hidden')}
                    </p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {suggestion.shared_issues.slice(0, 2).map((issue, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {issue.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate(`/community/profile/${suggestion.profile.id}`)}
                    >
                      {language === 'es' ? 'Ver Perfil' : 'View Profile'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coalition Activity Feed */}
      <Card data-testid="activity-feed-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            {language === 'es' ? 'Actividad de la Coalición' : 'Coalition Activity'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {feedLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : activityFeed.length > 0 ? (
            <ScrollArea className="h-[320px] pr-4">
              <div className="space-y-3">
                {activityFeed.map((item, index) => (
                  <div 
                    key={item.id || index}
                    className={`p-3 rounded-lg border ${getFeedColor(item.type, item.risk_score)} cursor-pointer hover:opacity-90 transition-opacity`}
                    onClick={() => {
                      if (item.type === 'policy_alert' && item.policy_id) {
                        navigate(`/policies/${item.policy_id}`);
                      } else if (item.type === 'group_suggestion' && item.group_id) {
                        navigate('/community/groups');
                      } else if (item.type === 'connection_request') {
                        navigate('/community/connections');
                      } else if (item.type === 'group_activity' && item.group_id) {
                        navigate('/community/groups');
                      }
                    }}
                    data-testid={`feed-item-${index}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getFeedIcon(item.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-sm font-medium truncate">{item.title}</h4>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {formatTimeAgo(item.created_at)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                          {item.description}
                        </p>
                        {item.type === 'policy_alert' && item.risk_score && (
                          <div className="flex items-center gap-2 mt-2">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                item.risk_score >= 70 ? 'border-destructive text-destructive' :
                                item.risk_score >= 40 ? 'border-warning text-warning' :
                                'border-success text-success'
                              }`}
                            >
                              {item.risk_score >= 70 ? (language === 'es' ? 'Alto Riesgo' : 'High Risk') :
                               item.risk_score >= 40 ? (language === 'es' ? 'Riesgo Medio' : 'Medium Risk') :
                               (language === 'es' ? 'Bajo Riesgo' : 'Low Risk')}
                            </Badge>
                            {item.jurisdiction && (
                              <Badge variant="secondary" className="text-xs">
                                {item.jurisdiction}
                              </Badge>
                            )}
                          </div>
                        )}
                        {item.type === 'group_suggestion' && item.issue_areas && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {item.issue_areas.slice(0, 2).map((area, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {area.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <ArrowRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{language === 'es' ? 'No hay actividad reciente' : 'No recent activity'}</p>
              <p className="text-xs mt-1">
                {language === 'es' 
                  ? 'Conecta con defensores para ver actividad aquí'
                  : 'Connect with advocates to see activity here'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/community/groups')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-medium">{language === 'es' ? 'Explorar Grupos' : 'Explore Groups'}</h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'es' ? 'Únete a coaliciones y campañas' : 'Join coalitions and campaigns'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/settings/privacy')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-medium">{language === 'es' ? 'Configuración de Privacidad' : 'Privacy Settings'}</h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'es' ? 'Controla tu visibilidad y datos' : 'Control your visibility and data'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CommunityHub;
